import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Wand2, Loader2, X, ArrowRight, Search, UserPlus, CheckCircle2 } from "lucide-react";
import { useEscapeKey } from "@/hooks/useEscapeKey";
import { NewClientForm } from "@/components/NewClientForm";
import { RecordFormModal } from "@/components/RecordFormModal";
import { useClientsPickerQuery } from "@/hooks/usePortalQueries";

// Dashboard 1B redesign (2026-02), Part 2 — a thin entry point into the EXISTING AI Project
// Intake Copilot (`modules/ai_intake.py`, unchanged) and EXISTING Client-creation UI
// (`NewClientForm`, unchanged). This component owns ONLY: (a) kicking off analyze/clarify on
// the existing session, (b) resolving a client (match-or-create — the one genuinely new piece,
// backed by the new deterministic `GET /{id}/client-match`), and (c) handing off to the
// EXISTING `RecordFormModal` (type="project") for the actual create — never a second/parallel
// project-creation form. The full multi-turn workspace at /ai-assistant is untouched and
// unaffected by anything here.
const FACT_ROWS = [
  ["project_name", "Project name"],
  ["project_type_raw", "Type"],
  ["deliverables", "Deliverables"],
  ["shoot_dates", "Shoot dates"],
  ["deadline", "Delivery date"],
  ["shoot_location", "Location"],
  ["budget", "Budget hint"],
  ["special_constraints", "Notes"],
];

export function DashboardAiIntakeBar() {
  const [rawText, setRawText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [session, setSession] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [answering, setAnswering] = useState(false);

  const [match, setMatch] = useState(null); // {status, query, client_id, client_name}
  const [resolvedClient, setResolvedClient] = useState(null); // {id, name}
  const { data: clients = [] } = useClientsPickerQuery(!!session);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateClient, setShowCreateClient] = useState(false);

  const [fieldOptions, setFieldOptions] = useState({ category: [], production_model: [] });
  const [packages, setPackages] = useState([]);
  const [savingOverride, setSavingOverride] = useState(null); // which key is currently PATCHing

  const [prefillValues, setPrefillValues] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);

  const open = !!session;
  useEscapeKey(() => { if (!showCreateClient && !showProjectModal) closeSheet(); }, open);

  useEffect(() => {
    if (!session) return;
    api.get(`/v1/ai-intake/${session.id}/client-match`).then((r) => {
      setMatch(r.data);
      if (r.data.status === "matched") setResolvedClient({ id: r.data.client_id, name: r.data.client_name });
    }).catch(() => {});
    api.get("/v1/projects/field-options").then((r) => setFieldOptions(r.data || {})).catch(() => {});
    api.get("/v1/commercial/packages").then((r) => setPackages(r.data.items || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Sanity-check/override before saving (not applied silently) — a PM edit here PATCHes
  // `pm_overrides` on the EXISTING session (the same mechanism /prefill already reads first,
  // before falling back to the AI's own recommendation), so overriding costs zero new backend
  // code and the choice is respected everywhere this session is used later (project creation
  // AND the later Quote Builder handoff for `package_id`).
  const setOverride = async (key, value) => {
    setSavingOverride(key);
    try {
      const r = await api.patch(`/v1/ai-intake/${session.id}`, { pm_overrides: { [key]: value } });
      setSession(r.data);
    } catch {
      toast.error("Couldn't save that change");
    } finally {
      setSavingOverride(null);
    }
  };

  const draftProject = async () => {
    const text = rawText.trim();
    if (!text || analyzing) return;
    setAnalyzing(true);
    try {
      const r = await api.post("/v1/ai-intake/analyze", { raw_text: text });
      setSession(r.data);
      setResolvedClient(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Couldn't read that brief. Try again");
    } finally {
      setAnalyzing(false);
    }
  };

  const submitAnswers = async () => {
    const t = answerText.trim();
    if (!t || answering) return;
    setAnswering(true);
    try {
      const r = await api.post(`/v1/ai-intake/${session.id}/clarify`, { answers_text: t });
      setSession(r.data);
      setAnswerText("");
    } catch {
      toast.error("Couldn't update the recommendation");
    } finally {
      setAnswering(false);
    }
  };

  const closeSheet = () => {
    setSession(null);
    setRawText("");
    setMatch(null);
    setResolvedClient(null);
    setShowSearch(false);
    setSearchQuery("");
  };

  const filteredClients = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return clients.slice(0, 8);
    return clients.filter((c) => (c.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [clients, searchQuery]);

  const continueToProject = async () => {
    if (!resolvedClient) { toast.error("Resolve the client first: match an existing one or create a new one"); return; }
    try {
      const r = await api.get(`/v1/ai-intake/${session.id}/prefill`);
      setPrefillValues(r.data);
      setShowProjectModal(true);
    } catch {
      toast.error("Couldn't prepare the project draft");
    }
  };

  const onProjectCreated = async (record) => {
    try { await api.post(`/v1/ai-intake/${session.id}/link-project`, { project_id: record.id }); } catch {}
    setShowProjectModal(false);
    closeSheet();
  };

  const facts = session?.extracted_facts || {};
  const clarifyingQuestions = session?.clarifying_questions || [];
  const overrides = session?.pm_overrides || {};
  const catRec = session?.category_recommendation || {};
  const pmRec = session?.production_model_recommendation || {};
  const pkgRec = session?.package_recommendation || {};
  const currentCategory = overrides.category ?? catRec.category ?? "";
  const currentModel = overrides.production_model ?? pmRec.model ?? "";
  const currentPackageId = overrides.package_id ?? pkgRec.package_id ?? "";
  const currentPackage = packages.find((p) => p.package_id === currentPackageId);

  return (
    <div data-testid="dashboard-ai-intake-bar">
      {!open && (
        <div className="rounded-lg border border-hairline bg-paper flex items-center gap-3 px-4 py-3">
          <Wand2 className="h-4 w-4 text-brand shrink-0" />
          <input
            data-testid="ai-intake-bar-input"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") draftProject(); }}
            placeholder="Paste an email, brief or notes, or describe a new project…"
            className="flex-1 bg-transparent text-sm text-ink placeholder:text-faint focus:outline-none"
          />
          <button
            data-testid="ai-intake-bar-submit"
            onClick={draftProject}
            disabled={!rawText.trim() || analyzing}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-brand px-3.5 py-1.5 text-xs font-semibold text-ink hover:bg-brand-dim disabled:opacity-40 transition-colors"
          >
            {analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Draft project <ArrowRight className="h-3 w-3" /></>}
          </button>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10" data-testid="ai-intake-review-sheet">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeSheet} />
          <div className="relative w-full max-w-xl rounded-lg border border-hairline bg-paper shadow-2xl">
            <div className="flex items-center justify-between border-b border-hairline px-5 py-4">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-brand" />
                <h2 className="font-editorial text-lg tracking-tight">AI Project Draft</h2>
              </div>
              <button data-testid="ai-intake-sheet-close" onClick={closeSheet} className="text-faint hover:text-ink"><X className="h-4 w-4" /></button>
            </div>

            <div className="p-5 space-y-5">
              {clarifyingQuestions.length > 0 && (
                <div className="rounded-md border border-brand/30 bg-brand/5 p-3.5" data-testid="ai-intake-clarifying-questions">
                  <div className="text-[10px] uppercase tracking-wider text-faint mb-2">Before you continue: AI has questions</div>
                  <ul className="text-sm text-ink space-y-1 list-disc list-inside">
                    {clarifyingQuestions.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                  <textarea
                    data-testid="ai-intake-answers-input"
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder="Answer above, or leave blank and edit fields manually below"
                    rows={2}
                    className="mt-2.5 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:border-brand/50"
                  />
                  <button data-testid="ai-intake-submit-answers" onClick={submitAnswers} disabled={!answerText.trim() || answering}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-ink hover:bg-brand-dim disabled:opacity-40">
                    {answering && <Loader2 className="h-3 w-3 animate-spin" />} Update recommendation
                  </button>
                </div>
              )}

              {/* Client resolution — the genuinely new piece of Part 2, everything else below
                  is a read-only reflection of the existing session's extracted_facts. */}
              <div data-testid="ai-intake-client-row">
                {resolvedClient ? (
                  <div className="flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3.5 py-2.5">
                    <div className="flex items-center gap-2 text-sm text-emerald-700">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      Client: <span className="font-medium">{resolvedClient.name}</span>
                      {match?.status === "matched" && resolvedClient.id === match.client_id && <span className="text-[10px] text-emerald-700/70">(matched)</span>}
                    </div>
                    <button data-testid="ai-intake-client-change" onClick={() => setShowSearch(true)} className="text-[11px] text-faint hover:text-ink underline">Change</button>
                  </div>
                ) : (
                  <div className="rounded-md border border-orange-500/30 bg-orange-500/5 px-3.5 py-2.5">
                    <div className="text-sm text-orange-700">Client not recognised{match?.query ? `: "${match.query}"` : ""}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <button data-testid="ai-intake-match-existing" onClick={() => setShowSearch((s) => !s)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-2.5 py-1 text-[11px] text-ink hover:bg-surface2">
                        <Search className="h-3 w-3" /> Match existing
                      </button>
                      <button data-testid="ai-intake-create-client" onClick={() => setShowCreateClient(true)}
                        className="inline-flex items-center gap-1.5 rounded-md bg-brand px-2.5 py-1 text-[11px] font-medium text-ink hover:bg-brand-dim">
                        <UserPlus className="h-3 w-3" /> Create client
                      </button>
                    </div>
                  </div>
                )}
                {showSearch && (
                  <div className="mt-2 rounded-md border border-hairline bg-paper">
                    <input
                      autoFocus
                      data-testid="ai-intake-client-search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search clients…"
                      className="w-full border-b border-hairline px-3 py-2 text-sm bg-transparent text-ink placeholder:text-faint focus:outline-none"
                    />
                    <div className="max-h-40 overflow-y-auto">
                      {filteredClients.map((c) => (
                        <button key={c.id} data-testid={`ai-intake-client-option-${c.id}`}
                          onClick={() => { setResolvedClient({ id: c.id, name: c.name }); setShowSearch(false); setSearchQuery(""); }}
                          className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-surface2">
                          {c.name}
                        </button>
                      ))}
                      {filteredClients.length === 0 && <div className="px-3 py-2.5 text-xs text-faint">No matching clients</div>}
                    </div>
                  </div>
                )}
              </div>

              {/* Surfaced visibly (not applied silently) so the PM can sanity-check/override
                  the AI's category/production-model/package call BEFORE saving — each select
                  edit PATCHes `pm_overrides` on this same session via `setOverride` above. */}
              <div className="rounded-md border border-hairline divide-y divide-hairline" data-testid="ai-intake-recommendation-block">
                <div className="px-3.5 py-2.5 flex items-center justify-between gap-3">
                  <div className="text-[11px] uppercase tracking-wider text-faint w-24 shrink-0">Type</div>
                  <select
                    data-testid="ai-intake-category-select"
                    value={currentCategory}
                    disabled={savingOverride === "category"}
                    onChange={(e) => setOverride("category", e.target.value)}
                    className="flex-1 bg-transparent text-sm text-ink text-right focus:outline-none disabled:opacity-50"
                  >
                    {!currentCategory && <option value="">Select…</option>}
                    {fieldOptions.category?.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="px-3.5 py-2.5 flex items-center justify-between gap-3">
                  <div className="text-[11px] uppercase tracking-wider text-faint w-24 shrink-0">Production model</div>
                  <select
                    data-testid="ai-intake-production-model-select"
                    value={currentModel}
                    disabled={savingOverride === "production_model"}
                    onChange={(e) => setOverride("production_model", e.target.value)}
                    className="flex-1 bg-transparent text-sm text-ink text-right focus:outline-none disabled:opacity-50"
                  >
                    {!currentModel && <option value="">Manual Review</option>}
                    {fieldOptions.production_model?.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="px-3.5 py-2.5 flex items-center justify-between gap-3">
                  <div className="text-[11px] uppercase tracking-wider text-faint w-24 shrink-0">Suggested package</div>
                  <select
                    data-testid="ai-intake-package-select"
                    value={currentPackageId}
                    disabled={savingOverride === "package_id"}
                    onChange={(e) => setOverride("package_id", e.target.value)}
                    className="flex-1 bg-transparent text-sm text-ink text-right focus:outline-none disabled:opacity-50"
                  >
                    <option value="">None confidently matched</option>
                    {packages.map((p) => <option key={p.package_id} value={p.package_id}>{p.package_name}</option>)}
                  </select>
                </div>
                {(pmRec.reason || pkgRec.reason) && (
                  <div className="px-3.5 py-2 text-[11px] text-faint italic">
                    {pmRec.reason && <div>Model: {pmRec.reason}</div>}
                    {pkgRec.reason && <div className="mt-0.5">Package{currentPackage ? ` (${currentPackage.package_name})` : ""}: {pkgRec.reason}</div>}
                  </div>
                )}
              </div>

              <div className="divide-y divide-hairline" data-testid="ai-intake-fact-list">
                {FACT_ROWS.map(([key, label]) => {
                  const f = facts[key] || {};
                  return (
                    <div key={key} className="flex items-start justify-between gap-4 py-2.5">
                      <div className="text-[11px] uppercase tracking-wider text-faint pt-0.5 shrink-0 w-32">{label}</div>
                      <div className="text-sm text-ink text-right flex-1">
                        {f.value ? f.value : <span className="text-faint italic">not mentioned</span>}
                        {f.status === "inferred" && f.value && <span className="ml-1.5 text-[10px] text-brand-dim">(AI-inferred)</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button data-testid="ai-intake-cancel" onClick={closeSheet} className="rounded-md border border-hairline px-3.5 py-2 text-sm text-faint hover:text-ink">Cancel</button>
                <button data-testid="ai-intake-continue" onClick={continueToProject} disabled={!resolvedClient}
                  className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-40 transition-colors">
                  Create project <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <NewClientForm
        open={showCreateClient}
        onClose={() => setShowCreateClient(false)}
        onCreated={(client) => { setResolvedClient({ id: client.id, name: client.name }); setMatch((m) => ({ ...m, status: "created" })); }}
      />

      {session && (
        <RecordFormModal
          type="project"
          mode="create"
          open={showProjectModal}
          onClose={() => setShowProjectModal(false)}
          defaultClientId={resolvedClient?.id}
          defaultClientName={resolvedClient?.name}
          initialCreateValues={prefillValues || {}}
          onCreated={onProjectCreated}
        />
      )}
    </div>
  );
}
